// apiClient — wrapper delgado sobre fetch para hablar con el backend
// de CoopNet (Tracker.Api). Adjunta el JWT de Ecolab en cada request y
// normaliza el manejo de errores.
//
// El JWT en sí lo obtiene ecolabAuth.js (login directo contra Ecolab,
// nunca contra este backend — ver INSTRUCTIVO_TURNSTILE_APPS_FINAL.md).
// Este cliente solo lo REUSA para llamar a los endpoints de negocio de
// CoopNet.

const BASE_URL = process.env.REACT_APP_COOPNET_API_BASE_URL;
const TOKEN_STORAGE_KEY = 'coopnet_jwt';

export function getToken() {
  return localStorage.getItem(TOKEN_STORAGE_KEY);
}

export function setToken(token) {
  if (token) localStorage.setItem(TOKEN_STORAGE_KEY, token);
  else localStorage.removeItem(TOKEN_STORAGE_KEY);
}

export class ApiError extends Error {
  constructor(message, status, body) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.body = body;
  }
}

/**
 * @param {string} path - ej. '/api/v1/cooperativas/{id}/contabilidad/plan-cuentas'
 * @param {object} options - { method, body, headers, skipAuth }
 */
export async function apiFetch(path, options = {}) {
  const { method = 'GET', body, headers = {}, skipAuth = false } = options;

  const finalHeaders = { 'Content-Type': 'application/json', ...headers };
  if (!skipAuth) {
    const token = getToken();
    if (token) finalHeaders['Authorization'] = `Bearer ${token}`;
  }

  let response;
  try {
    response = await fetch(`${BASE_URL}${path}`, {
      method,
      headers: finalHeaders,
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });
  } catch (networkError) {
    throw new ApiError(
      `No se pudo conectar con el servidor de CoopNet (${BASE_URL}). ¿Está corriendo el backend?`,
      0,
      null
    );
  }

  // 401/403: el JWT expiró, es inválido, o el usuario no tiene el rol
  // requerido. No forzamos logout automático aquí — quien llama decide
  // (por ejemplo AuthContext sí puede querer cerrar sesión en un 401).
  if (response.status === 204) return null;

  const contentType = response.headers.get('content-type') || '';
  const data = contentType.includes('application/json') ? await response.json().catch(() => null) : null;

  if (!response.ok) {
    const message = data?.message || `Error ${response.status} al llamar ${path}`;
    throw new ApiError(message, response.status, data);
  }

  return data;
}

export const api = {
  get: (path) => apiFetch(path, { method: 'GET' }),
  post: (path, body) => apiFetch(path, { method: 'POST', body }),
  put: (path, body) => apiFetch(path, { method: 'PUT', body }),
  delete: (path) => apiFetch(path, { method: 'DELETE' }),
};
